import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { IResourceComponentsProps, useNavigation } from '@refinedev/core';
import { useForm } from '@refinedev/antd';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Form, Input, Spin, Tabs } from 'antd';
import type { TabsProps } from 'antd';
import { Button } from '../../../components/ui/button';
import {
    FileUploader,
    UploadResult,
    UploadedFileMeta,
} from '../../../components/file-uploader';
import { api } from '../../../utils/api';
import { useNotification } from '../../../hooks/useNotification';
import { InternalLayout } from '../../../layouts/InternalLayout';
import styles from './edit.module.scss';
import {
    ApplicationSummary,
    AttachmentsList,
    MedicationSummary,
} from './components';
import {
    ValidationResults,
    Expertises,
    Changes,
    DocumentHistory,
    StateHistory,
    Submissions,
} from '../../../components/applications';
import { formatDateLabel } from '../../../utils/format';
import { hasMeaningfulValue } from './utils';

const tabKey = {
    APPLICATION: 'application',
    VALIDATION: 'validation',
    EXPERTISE: 'expertise',
    CHANGES: 'changes',
    DOCUMENTS: 'documents',
    SUBMISSIONS: 'submissions',
    STATE_HISTORY: 'state_history',
};

type TimelineItem = {
    key: string;
    title: string;
    date?: string | null;
};

export const ApplicationEdit: FC<IResourceComponentsProps> = () => {
    const { id } = useParams();
    const { replace } = useNavigation();
    const { msgSuccess, msgError } = useNotification();
    const { t } = useTranslation();

    const { formProps, formLoading, queryResult } = useForm<any>({
        action: 'edit',
        resource: 'applications',
        id,
        mutationMode: 'pessimistic',
    });

    const form = formProps?.form;
    const dataSource = queryResult?.data?.data;
    const paymentCheck = dataSource?.payment_check ?? [];
    const [fileError, setFileError] = useState<string | null>(null);
    const [uploadedFileNames, setUploadedFileNames] = useState<string[]>([]);
    const [localUploads, setLocalUploads] = useState<UploadedFileMeta[]>([]);
    const [uploaderResetSignal, setUploaderResetSignal] = useState<
        number | null
    >(null);

    const statuses = useMemo(() => {
        // @ts-ignore existing data shape
        return paymentCheck.map((record: any) => {
            const mapStatus = (value: string | null) => {
                switch (value) {
                    case 'not_payed':
                        return 'не оплачено';
                    case 'partial':
                        return 'частково оплачено';
                    case null:
                        return '-';
                    default:
                        return 'оплачено';
                }
            };

            const status = {
                number: record.number,
                expertise_status: mapStatus(record.expertise),
                state_collection_status: mapStatus(record.state_collection),
                not_expired_status: record.not_expired
                    ? 'не прострочено'
                    : 'прострочено',
                date: record.date,
            };

            if (record.parent !== null) {
                // @ts-ignore
                status.is_package = true;
            }

            return status;
        });
    }, [paymentCheck]);

    const paymentStatus = statuses?.[0];

    const fileLocked = useMemo(
        () =>
            statuses.filter(
                (status: any) =>
                    status.state_collection_status === 'не оплачено' ||
                    status.state_collection_status === 'частково оплачено' ||
                    status.expertise_status === 'не оплачено' ||
                    status.expertise_status === 'частково оплачено' ||
                    status.not_expired_status === 'прострочено'
            ).length,
        [statuses]
    );

    const requiredUploadMessage = t(
        'applications.wizard.required_upload',
        'Будь ласка, завантажте файл.'
    );

    useEffect(() => {
        if (formProps?.initialValues?.file_id) {
            setFileError(null);
        }
    }, [formProps?.initialValues?.file_id]);

    const handleSuccess = useCallback(
        (fileId: UploadResult) => {
            if (fileId) {
                form?.setFieldsValue({ file_id: fileId });
                setFileError(null);
            } else {
                form?.setFieldsValue({ file_id: null });
                setFileError(requiredUploadMessage);
            }
        },
        [form, requiredUploadMessage]
    );

    const resetUploadState = useCallback(
        async ({ triggerUploaderReset = true } = {}) => {
            form?.setFieldsValue({ file_id: null });
            setFileError(requiredUploadMessage);
            setUploadedFileNames([]);
            setLocalUploads([]);
            if (triggerUploaderReset) setUploaderResetSignal(Date.now());
        },
        [form, requiredUploadMessage]
    );

    const handleFinish = async (values: any): Promise<boolean> => {
        try {
            const formFields = {
                file_id: values.file_id,
            };

            await api.patch(`/api/applications/${id}`, formFields);
            await queryResult?.refetch?.();

            msgSuccess(
                t(
                    'notifications.the_upload_is_complete_and_wait_for_it_to_be_processed_successfully_check_the_status_of_dossier_processing_after_some_time'
                )
            );

            setFileError(null);
            return true;
        } catch (error: any) {
            console.error(error);
            const msg = error?.response?.data?.message;
            msgError(t('notifications.entry_error') ?? msg);
            return false;
        }
    };

    const handleSave = async () => {
        if (!form) return;

        try {
            const values = await form.validateFields();
            const ok = await handleFinish(values);

            if (!ok) {
                return;
            }
        } catch (error: any) {
            const hasFileError = Array.isArray(error?.errorFields)
                ? error.errorFields.some((field: any) => {
                      if (!field?.name) return false;
                      return Array.isArray(field.name)
                          ? field.name.includes('file_id')
                          : field.name === 'file_id';
                  })
                : false;

            if (hasFileError) {
                setFileError(requiredUploadMessage);
            }
        }
    };

    const attachments = useMemo(() => {
        if (Array.isArray(dataSource?.files) && dataSource.files.length) {
            return dataSource.files as any[];
        }

        if (dataSource?.file) {
            return [dataSource.file];
        }

        return [];
    }, [dataSource]);

    const handleUploadedFilesChange = useCallback((names: string[]) => {
        setUploadedFileNames(names);
    }, []);

    const handleLocalFilesChange = useCallback((files: UploadedFileMeta[]) => {
        setLocalUploads(files);
        setUploadedFileNames(files.map(file => file.name).filter(Boolean));
    }, []);

    const existingUploaderFiles = useMemo(
        () =>
            attachments
                .map(
                    file =>
                        file?.original_name ??
                        file?.name ??
                        file?.file_name ??
                        file?.filename ??
                        ''
                )
                .filter((name): name is string => Boolean(name)),
        [attachments]
    );

    const uploaderFiles = useMemo(() => {
        const names = new Set<string>(existingUploaderFiles);
        uploadedFileNames.forEach(name => {
            if (name) names.add(name);
        });
        return Array.from(names);
    }, [existingUploaderFiles, uploadedFileNames]);

    const combinedAttachments = useMemo(() => {
        const existingNames = new Set(existingUploaderFiles);

        const localFromMeta = localUploads
            .filter(file => file.name && !existingNames.has(file.name))
            .map(file => ({
                id: `local-${file.name}`,
                name: file.name,
                original_name: file.name,
                created_at: file.uploadedAt ?? new Date().toISOString(),
                size: file.size,
                removable: true,
            }));

        const localFromNames =
            localFromMeta.length > 0
                ? []
                : uploadedFileNames
                      .map(name => name?.trim())
                      .filter(name => Boolean(name) && !existingNames.has(name))
                      .map(name => ({
                          id: `local-${name}`,
                          name,
                          original_name: name,
                          created_at: new Date().toISOString(),
                          removable: true,
                      }));

        return [...localFromMeta, ...localFromNames, ...attachments];
    }, [attachments, existingUploaderFiles, localUploads, uploadedFileNames]);

    const handleRemoveAttachment = useCallback(
        async (file: any) => {
            const name =
                file?.name ??
                file?.original_name ??
                file?.file_name ??
                file?.filename;

            if (!file?.removable || !name) return;

            await resetUploadState();
        },
        [resetUploadState]
    );

    const statusTimeline = useMemo<TimelineItem[]>(() => {
        const history = Array.isArray(dataSource?.state_history)
            ? dataSource.state_history
            : [];

        const normalizeTitle = (title: string) => title?.trim?.() ?? '';
        const hasMeaningfulTitle = (title: string) => {
            const clean = normalizeTitle(title);
            return (
                Boolean(clean) &&
                clean !== '-' &&
                clean !== '—' &&
                clean !== '–'
            );
        };

        const resolveCurrentStatus = () => {
            const statusField = dataSource?.status;

            let code: string | undefined;
            let name: string | undefined;

            if (typeof statusField === 'string') {
                code = statusField;
            } else if (statusField && typeof statusField === 'object') {
                code =
                    statusField.code ??
                    statusField.status_code ??
                    statusField.state_code ??
                    undefined;
                name =
                    statusField.name ??
                    statusField.status_name ??
                    statusField.state_name ??
                    undefined;
            }

            if (!code) {
                code = dataSource?.status_code ?? dataSource?.state_code;
            }

            if (!name) {
                name = dataSource?.status_name ?? dataSource?.state_name;
            }

            const translated = code
                ? t(`applications.fields.${code}`, name ?? code)
                : null;

            const title = name ?? translated ?? code ?? null;

            return title
                ? {
                      title,
                      date:
                          dataSource?.updated_at ??
                          dataSource?.status_updated_at ??
                          null,
                  }
                : null;
        };

        const currentStatus = resolveCurrentStatus();

        const currentItem = currentStatus
            ? [
                  {
                      key: 'current-status',
                      title: currentStatus.title,
                      date: currentStatus.date,
                  },
              ]
            : [];

        const historyItems = history
            .map((entry: any, index: number) => ({
                key: String(index),
                title:
                    entry?.state?.name ??
                    entry?.state_name ??
                    entry?.status_name ??
                    '-',
                date:
                    entry?.changedOn ?? entry?.changed_on ?? entry?.created_at,
            }))
            .filter((item: TimelineItem) => {
                return hasMeaningfulTitle(item.title);
            });

        return [...currentItem, ...historyItems].filter(
            (item: TimelineItem) => {
                return hasMeaningfulTitle(item.title);
            }
        );
    }, [dataSource, t]);

    const applicationNumber =
        dataSource?.registration_certificate_number ||
        dataSource?.moh_application_number ||
        dataSource?.certificate?.number;

    const applicationDateRaw = [
        dataSource?.registration_certificate_date,
        dataSource?.moh_application_date,
    ].find(hasMeaningfulValue);

    const applicationDate = formatDateLabel(applicationDateRaw, '');
    const hasApplicationDate = hasMeaningfulValue(applicationDate);

    const summaryTitle = applicationNumber
        ? `№ ${applicationNumber}${
              hasApplicationDate
                  ? ` ${t(
                        'applications.wizard.application_date_suffix',
                        'від'
                    )} ${applicationDate}`
                  : ''
          }`
        : undefined;

    const fileUploaderSection = (
        <div className={styles.sectionBlock}>
            <section className={styles.uploadSection}>
                <div
                    className={`${styles.sectionHeadingRow} ${styles.attachmentsHeading}`}
                >
                    <span className={styles.sectionNumberFilled}>2</span>
                    <div className={styles.sectionHeadingGroup}>
                        <h2 className={styles.sectionHeading}>
                            {t(
                                'applications.wizard.attach_documents',
                                'Прикріпити документи'
                            )}
                        </h2>
                    </div>
                </div>
                <Form
                    form={form}
                    layout="vertical"
                    initialValues={formProps?.initialValues}
                >
                    <Form.Item
                        name="file_id"
                        hidden
                        rules={[
                            {
                                required: true,
                                message: requiredUploadMessage,
                            },
                        ]}
                    >
                        <Input type="hidden" />
                    </Form.Item>
                    <div className={styles.uploadCard}>
                        <FileUploader
                            variant="dropzone"
                            onSuccess={handleSuccess}
                            onRemove={() =>
                                resetUploadState({
                                    triggerUploaderReset: false,
                                })
                            }
                            disabled={!!fileLocked || formLoading}
                            onlyZip
                            autoStart
                            message={statuses}
                            accept=".zip"
                            onValidationError={setFileError}
                            existingFiles={uploaderFiles}
                            onFilesChange={handleUploadedFilesChange}
                            onLocalFilesChange={handleLocalFilesChange}
                            resetSignal={uploaderResetSignal}
                        />
                        {fileError && combinedAttachments.length === 0 && (
                            <div className={styles.uploadError}>
                                {fileError}
                            </div>
                        )}
                    </div>
                </Form>
                <h3 className={styles.uploadedTitle}>
                    {t(
                        'applications.wizard.uploaded_files',
                        'Завантажені файли'
                    )}
                </h3>
                <AttachmentsList
                    files={combinedAttachments}
                    onRemove={handleRemoveAttachment}
                />
                <div className={styles.actionsRow}>
                    <Button
                        variant="secondary"
                        onClick={() => replace('/applications')}
                    >
                        {t('buttons.back')}
                    </Button>
                    <Button variant="secondary" onClick={handleSave}>
                        {t('buttons.save')}
                    </Button>
                </div>
            </section>
        </div>
    );

    const tabItems: TabsProps['items'] = [
        {
            key: tabKey.APPLICATION,
            label: t('applications.fields.application'),
            children: (
                <div className={styles.dossierTabPane}>
                    <ApplicationSummary
                        data={dataSource}
                        paymentStatus={paymentStatus}
                        heading={t('applications.fields.application')}
                    />
                    <MedicationSummary
                        data={dataSource?.medication}
                        heading={t(
                            'applications.wizard.medicine_information',
                            'Інформація про лікарський засіб'
                        )}
                    />
                    {fileUploaderSection}
                </div>
            ),
        },
        {
            key: tabKey.VALIDATION,
            label: t('applications.titles.validation_results'),
            children: (
                <ValidationResults
                    data={dataSource?.ectd_errors ?? []}
                    group="applications.titles"
                />
            ),
        },
        {
            key: tabKey.EXPERTISE,
            label: t('applications.titles.expertises'),
            children: (
                <Expertises
                    data={dataSource?.expertises ?? []}
                    group="applications.titles"
                />
            ),
        },
        {
            key: tabKey.CHANGES,
            label: t('applications.titles.change_history'),
            children: (
                <Changes
                    data={dataSource?.changes ?? []}
                    group="applications.titles"
                />
            ),
        },
        {
            key: tabKey.STATE_HISTORY,
            label: t('applications.titles.state_history'),
            children: (
                <StateHistory
                    data={dataSource?.state_history ?? []}
                    group="applications.titles"
                />
            ),
        },
        {
            key: tabKey.DOCUMENTS,
            label: t('applications.titles.document_history'),
            children: (
                <DocumentHistory
                    data={dataSource?.print_history ?? []}
                    group="applications.titles"
                />
            ),
        },
        {
            key: tabKey.SUBMISSIONS,
            label: t('applications.titles.submissions'),
            children: (
                <Submissions
                    submissions={dataSource?.submissions ?? []}
                    status={dataSource?.status}
                />
            ),
        },
    ];

    const sidebar = (
        <aside className={styles.statusWrapper}>
            <h3 className={styles.statusTitle}>
                {t('applications.wizard.status_timeline', 'Статус заявки')}
            </h3>
            <ul className={styles.timeline}>
                {statusTimeline.length ? (
                    statusTimeline.map(item => (
                        <li key={item.key} className={styles.timelineItem}>
                            {item.title}
                        </li>
                    ))
                ) : (
                    <li className={styles.timelineItem}>
                        {t('applications.titles.no_data')}
                    </li>
                )}
            </ul>
        </aside>
    );

    return (
        <InternalLayout
            onBack={() => replace('/applications')}
            title={t('applications.wizard.dossier_title', 'Досьє')}
            contentTitle={summaryTitle}
            sidebar={sidebar}
        >
            <div className={styles.pageTransition}>
                <Spin spinning={formLoading}>
                    <Tabs
                        defaultActiveKey={tabKey.APPLICATION}
                        className={styles.dossierTabs}
                        items={tabItems}
                    />
                </Spin>
            </div>
        </InternalLayout>
    );
};

export default ApplicationEdit;
